import React from "react";
import {Button, ButtonGroup, createStyles, Drawer, withStyles} from "@material-ui/core";
import {withRouter} from "next/router";
import SectionsFilter from "../../components/SectionsFilter";
import SearchResultList from "../../components/SearchResult";
import {WithRouterProps} from "next/dist/client/with-router";
import Layout from "../../components/Layout"
import I18nMessage from "../../components/i18n/I18nMessage";
import PortalFontIcon from "../../components/PortalFontIcon";
import {ISection} from "@jorsek/content-portal-client/types";
import getGlobalProps from "../../utils/getGlobalProps";
import {toSearch} from "@jorsek/content-portal-utils";
import {checkAuth} from "../../utils/auth";
import RenderHtml from "../../components/RenderHtml";
import {assignSsoInfoProp} from "../../utils/PropUtil";
import HerettoDeployWindowObj, {ClientEvent} from "../../components/HerettoDeployWindowObj";
import {IPortalRuntimeConfig} from "server/types";
import {getClientSideConfig} from "../../utils/PageUtil";
import DocsAiAnswer from "../../components/DocsAiAnswer";
import '@jorsek/elements-core/styles-isolated.min.css';
import {multiReplaceLtGt} from "utils/string";

export const searchIndexStyles = createStyles((theme: any) => ({
    searchIndex: theme.searchIndex,
    searchIndexResults: theme.searchIndexResults,
    searchIndexResultsItem: theme.searchIndexResultsItem,
    searchIndexResultsTitle: theme.searchIndexResultsTitle,
    searchIndexNavbar: theme.searchIndexNavbar,
    searchIndexNavbarContainer: theme.searchIndexNavbarContainer,
    searchIndexSectionsFilter: theme.searchIndexSectionsFilter,
    searchIndexNavbarTitleContainer: theme.searchIndexNavbarTitleContainer,
    searchIndexPagination: theme.searchIndexPagination,
    searchIndexFiltersTitle: theme.searchIndexFiltersTitle,
    searchIndexFiltersButton: theme.searchIndexFiltersButton,
    searchIndexFiltersButtonGroup: theme.searchIndexFiltersButtonGroup,
    searchIndexFiltersButtonGroupMobile: theme.searchIndexFiltersButtonGroupMobile,
    searchIndexApplyFiltersButton: theme.searchIndexApplyFiltersButton,
    searchIndexFilterDrawer: theme.searchIndexFilterDrawer,
    searchIndexFilterDrawerContainer: theme.searchIndexFilterDrawerContainer,
    searchIndexClearFiltersButton: theme.searchIndexClearFiltersButton,
    searchIndexClearFiltersButtonMobile: theme.searchIndexClearFiltersButtonMobile
}));

// @ts-ignore
interface ISearchIndexProps extends WithRouterProps {
    runtimeConfig: any;
    layoutProps: any;
    path: string;
    section: string;
    currentPage: number;
    searchText: string;
    searchSections: ISection[];
    externalSites: any[];
    siteSections: any[];
    searchFacets: any;
    searchEndpoint: any;
    searchResults: any;
    searchResultsTotal: any;
    classes: any;
}

type PendingRequest = {
    response: Promise<any> | undefined
    searchKey: string
    cancelled?: boolean
}

export type PageLink = {
    title: string
    href: string
}

export type AiAnswerCompletionInfo = {
    sourceLinks: PageLink[]
}

export type AiStreamResponse = {
    started: boolean
    message: string
    done: boolean
}

const buildSearchKey = (urlIncludingParams: string, locale: string) => {
    return `${urlIncludingParams}__locale${locale}`
}


const AI_SEARCH_ENABLED = false
const DEFAULT_SEARCH_ENDPOINT = '/api/search/'
const AI_SEARCH_ENDPOINT = '/api/aiSearch/'

export class SearchIndex extends React.Component<ISearchIndexProps & WithRouterProps> {

    _oldAsPath = '';
    _oldLocale = '';

    state = {
        siteSections: [] as any,
        drawerOpen: false,
        selectedFacets: [] as any,
        searchResults: [],
        searchResultsTotal: 0,
        aiStreamResponse: {
            started: false,
            message: '',
            done: false
        },
        aiCompletionInfo: null,
        loading: true,
    };
    currentSearchRequest: PendingRequest = undefined

    constructor(props) {
        super(props);
    }

    static defaultProps = {
        siteSections: [],
        externalSites: [],
        searchEndpoint: DEFAULT_SEARCH_ENDPOINT,
        searchResults: null,
        searchResultsTotal: 0
    }

    async appendText(text: string) {
        // setTimeout is to ensure the browser updates the page.
        // Without it, the page is not updated until the message has finished.
        setTimeout(() => {
            this.setState((prev) => {
                // @ts-ignore
                const existingMessage = prev?.aiStreamResponse?.message
                const updated = existingMessage + text
                return {
                    aiStreamResponse: {
                        message: updated,
                        started: true,
                        done: false
                    }
                }
            });
        }, 1)
    }

    aiAnswerEnabled() {
        return !!this.props.runtimeConfig.searchEnableAiAnswer
    }

    async makeSearchApiCall(urlIncludingParams: string, searchKey: string) {
        const aiSearch = this.aiAnswerEnabled()
        if (!aiSearch) {
            fetch(urlIncludingParams).then((response) => {
                response.json().then((searchResult) => {
                    this.setState({
                        searchResults: searchResult.results,
                        searchResultsTotal: searchResult.total_count,
                        loading: false
                    })
                })

            })
            return
        } else {
            this.makeAiSearchApiCall(searchKey, urlIncludingParams);
        }
    }

    private makeAiSearchApiCall(searchKey: string, urlIncludingParams: string) {
        this.setState({
            loading: true, aiStreamResponse: {
                started: false,
                message: '',
                done: false
            }
        })
        const pendingRequest: PendingRequest = {
            response: undefined,
            searchKey,
            cancelled: false
        }
        this.currentSearchRequest = pendingRequest
        const eventSource = new EventSource(`${urlIncludingParams}`)
        eventSource.onmessage = (event) => {
            if (pendingRequest.cancelled) {
                eventSource.close()
                return
            }
            const data = JSON.parse(event.data)
            const type = data.type
            if (type === 'searchResult') {
                const searchResult = data.data
                const hits = searchResult.hits.map(hit => {
                    hit.breadcrumbs = hit.breadcrumbs.filter(b => b.href !== "");
                    return hit;
                })
                this.setState({
                    searchResults: hits,
                    searchResultsTotal: searchResult.totalResults,
                    loading: false
                })
            } else if (type === 'answerTextChunk') {
                const answerChunk = data.data.answerChunk
                this.appendText(answerChunk)

            } else if (type === 'answerFinished') {
                eventSource.close()
                const sourceNavLinks = data.data.sources
                const aiCompletionInfo: AiAnswerCompletionInfo = {sourceLinks: sourceNavLinks}
                setTimeout(() => {
                    this.setState((prev) => {
                        return {
                            aiCompletionInfo,
                            aiStreamResponse: {
                                // @ts-ignore
                                ...prev.aiStreamResponse,
                                done: true
                            }
                        }
                    })
                }, 2)
            } else {
                console.log(`Received unknown message type: ${type}`)
            }
        }
    }

    async triggerSearchCall() {
        const searchEndpoint = this.aiAnswerEnabled() ? AI_SEARCH_ENDPOINT : DEFAULT_SEARCH_ENDPOINT
        const urlIncludingParams = `${searchEndpoint}${toSearch({
            ...this.props.router.query,
            locale: this.props.router.locale
        })}`
        const searchKey = buildSearchKey(urlIncludingParams, this.props.router.locale)
        let makeSearchCall = false
        if (this.currentSearchRequest) {
            const keyMatchesCurrentRequest = this.currentSearchRequest.searchKey === searchKey
            if (!keyMatchesCurrentRequest) {
                this.currentSearchRequest.cancelled = true
                makeSearchCall = true
            }
        } else {
            makeSearchCall = true
        }
        if (makeSearchCall) {
            this.makeSearchApiCall(urlIncludingParams, searchKey)
        }
    }

    async componentDidUpdate() {
        if ((this._oldAsPath !== this.props.router.asPath) || (this._oldLocale !== this.props.router.locale)) {
            this._oldAsPath = this.props.router.asPath;
            this._oldLocale = this.props.router.locale;
            try {
                this.triggerSearchCall();
                const scrollRoot = document.getElementById("scroll-root");
                scrollRoot && typeof (scrollRoot.scrollTo) === "function" && scrollRoot.scrollTo({
                    top: 0,
                    left: 0
                });
                setTimeout(() => HerettoDeployWindowObj.triggerEvent(ClientEvent.pageUpdate))
            } catch (ex) {
                console.error(ex.message);
            }

        }
    }

    get pageIndex(): number {
        const page = parseInt(this.props.router.query.page as string, 10) || 1;
        return page - 1;
    }

    get pageSize(): number {
        return parseInt(this.props.router.query.pageSize as string, 10) || 10;
    }

    async componentDidMount() {
        this._oldAsPath = this.props.router.asPath
        this.triggerSearchCall();
    }

    clearFilter() {
        const newQuery = Object.entries(this.props.router.query).reduce((acc, [k, v]) => {
            if (!k.startsWith("facet_")) {
                acc[k] = v;
            }
            return acc;
        }, {}) as any;
        delete newQuery.section;
        delete newQuery.page;
        return this.props.router.push({
            pathname: this.props.router.pathname,
            query: newQuery
        }, null, {shallow: true})
    }

    updateRouteQuery(key, value) {
        let {...nextQuery} = this.props.router.query;
        this.props.router.push({
            pathname: this.props.router.pathname,
            query: {
                ...nextQuery,
                //reset page unless the key is "page" which will override this implicitly.
                page: 1,
                [key]: value
            }
        }, null, {shallow: true, scroll: true});
    }

    render() {
        const {searchResults, searchResultsTotal} = this.state;
        // @ts-ignore
        return (
            <Layout {...this.props.layoutProps}
                    className={'search-index'}
                    section={this.props.router.query.section as any}
            >
                {}
                {this.props.runtimeConfig.htmlReplacements?.searchPage != null ?
                    <RenderHtml>
                        {this.props.runtimeConfig.htmlReplacements?.searchPage}
                    </RenderHtml> :
                    <>
                        <I18nMessage id="message.search.results"
                                     className={this.props.classes.searchIndexResultsTitle}
                                     values={{search: multiReplaceLtGt(this.props.router.query.queryString?.toString())}}/>
                        <div className={this.props.classes.searchIndex}>
                            <div className={this.props.classes.searchIndexNavbar}>
                                <div className={this.props.classes.searchIndexNavbarTitleContainer}>
                                    <I18nMessage id="label.filters"
                                                 className={this.props.classes.searchIndexFiltersTitle}/>
                                    <I18nMessage id="label.filter.clear"
                                                 Component={Button}
                                                 color={'inherit'}
                                                 className={this.props.classes.searchIndexClearFiltersButton}
                                                 onClick={() => this.clearFilter()}/>
                                </div>

                                <div className={this.props.classes.searchIndexNavbarContainer}>
                                    <div className={this.props.classes.searchIndexSectionsFilter}>
                                        <SectionsFilter
                                            onChangeSections={(type, val) => this.updateRouteQuery(type, val)}
                                            siteSections={this.props.siteSections}
                                            selectedFacets={this.state.selectedFacets}
                                            searchFacets={this.props.searchFacets}
                                            runtimeConfig={this.props.runtimeConfig}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={this.props.classes.searchIndexResults}>
                                <ButtonGroup
                                    className={this.props.classes.searchIndexFiltersButtonGroup}>
                                    <I18nMessage id="label.filter.results"
                                                 Component={Button}
                                                 color={'inherit'}
                                                 className={this.props.classes.searchIndexFiltersButton}
                                                 onClick={() => this.setState({drawerOpen: true})}/>
                                    <I18nMessage id="label.filter.clear"
                                                 Component={Button}
                                                 color={'inherit'}
                                                 onClick={() => this.clearFilter()}/>
                                </ButtonGroup>

                                <Drawer
                                    open={this.state.drawerOpen}
                                    onClose={() => this.setState({drawerOpen: false})}
                                    classes={{paper: this.props.classes.searchIndexFilterDrawer}}
                                >
                                    <ButtonGroup
                                        className={this.props.classes.searchIndexFiltersButtonGroupMobile}>
                                        <I18nMessage id="label.filter.apply"
                                                     Component={Button}
                                                     color={'inherit'}
                                                     className={this.props.classes.searchIndexApplyFiltersButton}
                                                     onClick={() => this.setState({drawerOpen: false})}
                                        />
                                        <Button variant="text"
                                                className={this.props.classes.searchIndexClearFiltersButtonMobile}
                                                onClick={() => this.setState({drawerOpen: false})}>
                                            <PortalFontIcon name="close"/>
                                        </Button>
                                    </ButtonGroup>
                                    <div
                                        className={this.props.classes.searchIndexFilterDrawerContainer}>
                                        <SectionsFilter
                                            onChangeSections={(type, val) => this.updateRouteQuery(type, val)}
                                            siteSections={this.props.siteSections}
                                            selectedFacets={this.state.selectedFacets}
                                            searchFacets={this.props.searchFacets}
                                            runtimeConfig={this.props.runtimeConfig}
                                        />
                                    </div>
                                </Drawer>
                                {
                                    this.state.loading ? <div>Loading...</div> :
                                        <div>

                                            {
                                                //@ts-ignore
                                                this.aiAnswerEnabled() && <DocsAiAnswer
                                                    aiStreamResponse={this.state.aiStreamResponse}
                                                    completionInfo={this.state.aiCompletionInfo}/>
                                            }
                                            <SearchResultList
                                                results={searchResults}
                                                resultsCount={searchResultsTotal}
                                                page={this.pageIndex}
                                                pageSize={this.pageSize}
                                                onPageClick={(_evt, _offset, p) => {
                                                    this.updateRouteQuery("page", p)
                                                }}
                                                paginationProps={{
                                                    classes: {root: this.props.classes.searchIndexPagination}
                                                }}
                                                // Normally we'd wrap the child component in withRouter so it can access the router properties, but that doesn't seem necessary in this instance
                                                searchQuery={this.props.router.query}
                                            />
                                        </div>
                                }

                            </div>
                        </div>
                    </>}
            </Layout>
        );
    }
}

const getFacets = (runtimeConfig: IPortalRuntimeConfig): string[] => {
    let facets = runtimeConfig?.search?.facets
    if (facets) {
        return facets
    }
    let facetsAdvancedSettings = runtimeConfig?.search?.facetsAdvancedSettings
    if (facets) {
        return Array.from(new Map(Object.entries(facetsAdvancedSettings)).keys())
    }
    return []
}

export async function getServerSideProps({req, locale}) {
    try {
        let props = {runtimeConfig: req.runtimeConfig} as any;
        if (req.runtimeConfig) {
            const [ssrProps, searchFacets] = await Promise.all([
                getGlobalProps(req.client, req.runtimeConfig, null, req.query, {
                    ["Accept-Language"]: locale
                }),
                req.client.getSearchFacets(getFacets(req.runtimeConfig),
                    {
                        ["Accept-Language"]: locale
                    })
            ]);

            searchFacets.unshift({
                param: "facet_section",
                id: "section",
                title: 'label.section',
                isUiLabel: true, // Indicates this is a UI label which needs to be converted up to a label.
                children: ssrProps.siteSections,
            })
            props.searchEndpoint = req.runtimeConfig.searchEndpoint || `${req.base_uri}${DEFAULT_SEARCH_ENDPOINT}`;


            ssrProps.searchFacets = searchFacets;
            props = ssrProps as any;
        }
        props.sectionVersions = null;
        props.structure = null;
        assignSsoInfoProp(props, req)
        props.runtimeConfig = getClientSideConfig(props.runtimeConfig)
        return checkAuth(req) || JSON.parse(JSON.stringify({props}));
    } catch (ex) {
        console.log("search.tsx", req.originalUrl, ex.message)
        throw ex;
    }
}


//@ts-ignore
export default withStyles(searchIndexStyles, {name: "SearchIndex"})(withRouter(SearchIndex)) as any;
